import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A dropdown that stays on the screen.
 *
 * A menu written as `absolute right-0 w-80` lines its right edge up with its
 * button and takes 320px leftwards from there. That is right on a desktop and
 * wrong on a phone, where the button is somewhere in the middle of a 390px
 * header: the panel's left half hangs off the side of the screen, and what is
 * cut off is the beginning of every line — the half you read first. The New
 * sheet templates were unreadable this way.
 *
 * So the panel is drawn against the window instead of against its parent:
 * measured from the trigger, clamped to the viewport, and narrowed if the
 * screen is narrower than the width asked for. The same arithmetic the
 * ribbon's own menus use, in a component the rest of the app can reach.
 *
 * Rendered in a portal, which is what lets it escape a parent with
 * `overflow: hidden` — every toolbar has one somewhere up the tree.
 */

/** Clear of the window's edges, so the panel never looks stuck to one. */
const EDGE = 8;

export function AnchoredPanel({
  trigger,
  width,
  align = 'right',
  className,
  children,
}: {
  /** The control the panel belongs to; it opens directly beneath it. */
  trigger: React.RefObject<HTMLElement | null>;
  /** How wide it would like to be. It gets less on a narrow screen. */
  width: number;
  /** Which of the trigger's edges the panel lines up with when there is room. */
  align?: 'left' | 'right';
  className?: string;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const element = trigger.current;
      if (!element) return;

      const bounds = element.getBoundingClientRect();
      const room = window.innerWidth - EDGE * 2;
      const actual = Math.min(width, room);

      // Where it would go if nothing were in the way, then pulled back inside
      // the window — which is the whole point of this component.
      const wanted = align === 'right' ? bounds.right - actual : bounds.left;
      const left = Math.max(EDGE, Math.min(wanted, window.innerWidth - actual - EDGE));

      setBox({ top: bounds.bottom + 4, left, width: actual });
    };

    place();

    // A panel pinned to window coordinates has to follow its trigger, or it
    // hangs in mid-air the moment anything moves underneath it.
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [trigger, width, align]);

  if (!box) return null;

  return createPortal(
    <div
      ref={panel}
      className={
        'fixed z-50 overflow-y-auto rounded-lg border border-border-strong bg-surface py-1 shadow-xl ' +
        (className ?? '')
      }
      style={{
        top: box.top,
        left: box.left,
        width: box.width,
        // Never taller than what is left of the window below it.
        maxHeight: `calc(100vh - ${Math.round(box.top) + EDGE}px)`,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
